import { motion } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAccountDisplayName } from "@/helpers";
import { TaskList } from "@/components/Task";
import { useConfigStore } from "@/stores/useConfigStore";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { useAnimatedList } from "@/hooks/useAnimatedList";

const listCardVariants = {
  initial: { opacity: 0, x: -24, scale: 0.97 },
  animate: { opacity: 1, x: 0, scale: 1 },
};

type TaskViewProps = {
  order: string[];
  account: any;
  listsMap: Map<string, any>;
  i: number;
  checkboxes: Record<string, Record<string, boolean>>;
  /** Matrix view on mobile: each account row is its own horizontal scroller */
  independentScroll?: boolean;
  /** Snap is lifted while a drag is in progress (see MainContent) */
  dragActive?: boolean;
};

export const ListView = ({ 
  order, 
  account, 
  listsMap, 
  checkboxes, 
}: TaskViewProps) => {
  const accountNickname = useConfigStore((state) => state.config.accounts[account.id]?.nickname);
  
  // Filter to visible list IDs so Draggable indices stay contiguous
  const visibleOrder = order.filter((listId) => {
    const list = listsMap.get(listId);
    if (!list || !list.id) return false;
    return checkboxes[account.id]?.[listId] ?? true;
  });

  const { exitingIds } = useAnimatedList(visibleOrder);

  return (
    <div className="relative px-[25%] max-md:px-4">
      <div className="mb-4 flex gap-4">
        <Avatar className={`h-6 w-6 ph-no-capture`}>
          <AvatarImage src={account?.picture} />
          <AvatarFallback>{`${account?.name?.charAt(0)}`}</AvatarFallback>
        </Avatar>
        <span className="min-w-max ph-no-capture">{getAccountDisplayName(account, accountNickname)}</span>
      </div>

      <Droppable droppableId={`account-lists-${account.id}`} type="list">
        {(droppableProvided) => (
          <div
            className="flex flex-col gap-4 pb-4"
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
          >
            {visibleOrder.map((listId, index) => {
              const list = listsMap.get(listId);
              if (!list) return null;

              return (
                <Draggable key={listId} draggableId={`list-${listId}`} index={index}>
                  {(draggableProvided) => (
                    <div
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      className="flex-1 w-full"
                    >
                      <motion.div
                        variants={listCardVariants}
                        initial="initial"
                        animate="animate"
                        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                      >
                        <TaskList
                          list={list}
                          account={account}
                          dragHandleProps={draggableProvided.dragHandleProps}
                        />
                      </motion.div>
                    </div>
                  )}
                </Draggable>
              );
            })}

            {exitingIds.map((listId) => {
              const list = listsMap.get(listId);
              if (!list) return null;
              return (
                <motion.div
                  key={`exit-${listId}`}
                  initial={{ opacity: 1, height: "auto" }}
                  animate={{ opacity: 0, height: 0, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                  className="overflow-hidden pointer-events-none"
                >
                  <TaskList list={list} account={account} />
                </motion.div>
              );
            })}

            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

export const GridView = ({
  order,
  account,
  listsMap,
  i,
  checkboxes,
  independentScroll,
  dragActive,
}: TaskViewProps) => {
  const accountNickname = useConfigStore((state) => state.config.accounts[account.id]?.nickname);

  const visibleOrder = order.filter((listId) => {
    const list = listsMap.get(listId);
    if (!list || !list.id) return false;
    return checkboxes[account.id]?.[listId] ?? true;
  });

  const { exitingIds } = useAnimatedList(visibleOrder);

  return (
    // scroll-snap-align is "<block> <inline>": accounts snap vertically to the
    // top of the outer scroller (start none), columns horizontally to the
    // center of their own row scroller (none center)
    <div className={`w-fit relative max-md:[scroll-snap-align:start_none] ${independentScroll ? "max-md:w-full" : ""}`}>
      <div className="mb-4 flex gap-4 items-center">
        <Avatar className={`h-6 w-6 ph-no-capture ${i === 0 ? 'ml-14 max-md:ml-0' : ''}`}>
          <AvatarImage src={account?.picture} />
          <AvatarFallback>{`${account?.name?.charAt(0)}`}</AvatarFallback>
        </Avatar>
        <span className="min-w-max ph-no-capture">{getAccountDisplayName(account, accountNickname)}</span>
      </div>

      <Droppable droppableId={`account-lists-${account.id}`} type="list" direction="horizontal">
        {(droppableProvided) => (
          <div
            // The row is its own horizontal scroller in mobile matrix view so
            // the browser axis-locks gestures: horizontal flicks scroll this
            // row only, vertical ones scroll the outer account scroller. The
            // overflow class must NOT toggle with dragActive — reflowing the
            // scroller mid-drag would break rbd's measurements; only snap
            // classes lift during drags.
            //
            // min-h-[500px] = the tallest a card can currently be (350px
            // pending-list cap in TaskList + ~150px card chrome), so rows
            // with short cards still offer a full-height horizontal touch
            // surface instead of a dead zone below the cards.
            className={`flex gap-4 pb-4 ${
              independentScroll
                ? `max-md:overflow-x-auto max-md:overscroll-x-contain max-md:scroll-px-4 max-md:min-h-[500px] ${
                    dragActive ? "" : "max-md:snap-x max-md:snap-mandatory"
                  }`
                : ""
            }`}
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
          >
            {visibleOrder.map((listId, index) => {
              const list = listsMap.get(listId);
              if (!list) return null;

              return (
                <Draggable key={listId} draggableId={`list-${listId}`} index={index}>
                  {(draggableProvided) => (
                    <div
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                    >
                      <motion.div
                        variants={listCardVariants}
                        initial="initial"
                        animate="animate"
                        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                        // max-md !important overrides beat the inline 300px so
                        // columns page one-at-a-time on phones
                        className="flex-1 min-w-max max-md:!w-[85vw] max-md:!min-w-[85vw] max-md:!max-w-[85vw] max-md:[scroll-snap-align:none_center]"
                        style={{ width: 300, maxWidth: 300, minWidth: 300 }}
                      >
                        <TaskList
                          list={list}
                          account={account}
                          dragHandleProps={draggableProvided.dragHandleProps}
                        />
                      </motion.div>
                    </div>
                  )}
                </Draggable>
              );
            })}

            {exitingIds.map((listId) => {
              const list = listsMap.get(listId);
              if (!list) return null;
              return (
                <motion.div
                  key={`exit-${listId}`}
                  initial={{ opacity: 1, width: 300 }}
                  animate={{ opacity: 0, width: 0, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                  className="overflow-hidden pointer-events-none flex-shrink-0"
                >
                  <div style={{ width: 300, minWidth: 300 }}>
                    <TaskList list={list} account={account} />
                  </div>
                </motion.div>
              );
            })}

            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};
